import { Box, FormControl, FormControlLabel, MenuItem, Select, Switch, TextField } from "@mui/material"
import { AddOutlined, HelpOutlined, KeyboardArrowDownOutlined } from "@mui-symbols-material/w300"
import React, { type FC } from "react"
import { Controller, useFieldArray, useFormContext } from "react-hook-form"

import { Button } from "@/components/common/Button"
import { PhoneNumberInput } from "@/components/common/Phone/PhoneInput"
import { FIELD_LABEL_CLASSES, TEXT_FIELD_STYLING_PROPS } from "@/constants/forms"
import { ContactType } from "@/graphql/codegen/graphql"
import type { ContactMethodsFormValues } from "@/schemas/contactFormSchema"
import { formatNumericInput } from "@/utils/number"

const ContactMethodFormFields: FC = () => {
  const { control, getValues } = useFormContext<ContactMethodsFormValues>()
  const { fields, append, update } = useFieldArray({
    control,
    name: "phones",
  })

  const handleAddPhone = () => append({ number: "", type: "Text", phoneExt: "", isPreferred: false })

  const handlePreferredPhoneChange = (preferredPhoneIndex: number) =>
    fields.forEach((field, index) => {
      const currentField = getValues(`phones.${index}`)
      update(index, { ...currentField, isPreferred: index === preferredPhoneIndex })
    })

  return (
    <Box>
      {fields.map((item, index) => (
        <Box key={item.id} className='mt-6'>
          <Box display='grid' gridTemplateColumns='5fr 1fr' gap={4}>
            <Box className='flex flex-row'>
              <PhoneNumberInput
                control={control}
                name={`phones.${index}.number`}
                label='Phone'
                placeholder='+1 (123) 456-7890'
                containerProps={{
                  className: "flex-1",
                  sx: { marginRight: "-1px" },
                }}
                sx={{
                  "& fieldset": {
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                  },
                }}
                endAdornment={<HelpOutlined className='text-gray-600' />}
              />

              <Controller
                name={`phones.${index}.type`}
                control={control}
                render={({ field }) => (
                  <FormControl className='mt-[22px] w-[120px]'>
                    <Select
                      {...field}
                      variant='outlined'
                      IconComponent={KeyboardArrowDownOutlined}
                      sx={{
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                      }}
                      data-testid={`contact-phone-type-select-${index}`}
                    >
                      {Object.keys(ContactType).map((type) => (
                        <MenuItem key={type} value={type} data-testid={`phone-type-${type.toLowerCase()}`}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Box>

            <Controller
              name={`phones.${index}.phoneExt`}
              control={control}
              render={({ field }) => (
                <Box className='relative'>
                  <label className={FIELD_LABEL_CLASSES} htmlFor={field.name}>
                    Ext
                  </label>

                  <TextField
                    {...field}
                    name={field.name}
                    {...TEXT_FIELD_STYLING_PROPS}
                    placeholder='-'
                    value={field.value ?? ""}
                    onChange={(e) => {
                      const formattedValue = formatNumericInput(e.target.value)
                      field.onChange(formattedValue.toString())
                    }}
                    slotProps={{
                      htmlInput: {
                        maxLength: 10,
                        className: "hide-stepper",
                      },
                    }}
                  />
                </Box>
              )}
            />
          </Box>

          <Box className='mt-4 flex justify-between'>
            {fields.length > 1 && (
              <Controller
                name={`phones.${index}.isPreferred`}
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        {...field}
                        checked={item.isPreferred}
                        onChange={() => handlePreferredPhoneChange(index)}
                      />
                    }
                    label='Preferred'
                  />
                )}
              />
            )}
            {index === fields.length - 1 && (
              <Button
                variant='primary'
                appearance='text'
                className={"ml-auto"}
                startIcon={<AddOutlined />}
                onClick={handleAddPhone}
              >
                Phone
              </Button>
            )}
          </Box>
        </Box>
      ))}
      <Controller
        name='email'
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Box className='mt-6'>
            <label htmlFor='contact-email' className={FIELD_LABEL_CLASSES}>
              Email
            </label>
            <TextField
              id='contact-email'
              {...field}
              {...TEXT_FIELD_STYLING_PROPS}
              slotProps={{
                ...TEXT_FIELD_STYLING_PROPS.slotProps,
                input: {
                  endAdornment: <HelpOutlined className='text-gray-600' />,
                },
              }}
              placeholder='name@domain.com'
              helperText={error?.message}
              error={!!error}
            />
          </Box>
        )}
      />
    </Box>
  )
}

export default ContactMethodFormFields
